import { GridRecord } from '../../components/Grid/GridDataTypes';
import { clientGroupForGrid } from '../../../pok/components/ClientGroups/ClientGroupSearch';
import {
  AddClientsToGroupDto,
  CreateUpdateClientGroupDto,
  CreateUpdateClientGroupDtoFromJSON,
  DefaultApi,
  GetClientGroupDto,
  GetLastEditorDto,
} from '../autogenerated/pokApiClient';
import { ClientGroupSchema } from '../validation/schemas';
import { validateAgainst } from '../validation/validateAgainst';
import { clientBasicForGrid } from '../../../pok/components/Clients/ClientWithContactsSearch';

export interface ClientGroupContextInterface {
  getById: (id: string) => Promise<GetClientGroupDto>;
  getAllByPortion: (
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCount: (filterText?: string, filterColumns?: string) => Promise<number>;
  create: (dto: CreateUpdateClientGroupDto) => Promise<GetClientGroupDto>;
  update: (id: string, dto: CreateUpdateClientGroupDto) => Promise<void>;
  addClients: (groupId: string, clientIds: string[]) => Promise<void>;
  removeItem: (itemId: string) => Promise<void>;
  getAllByNamePattern: (pattern: string) => Promise<GetClientGroupDto[]>;
  getAllByPortionForClient: (
    clientId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<GridRecord[]>;
  getAllCountForClient: (
    clientId: string,
    filterText?: string,
    filterColumns?: string,
  ) => Promise<number>;
  getLastEditor: (id: string) => Promise<GetLastEditorDto>;
  getWithoutContactTypeByPortion: (
    groupId: string,
    pageSize: number,
    pageNumber: number,
    orderBy?: string,
    orderDirection?: string,
    filterText?: string,
    filterColumns?: string,
    contactTypeId?: string,
  ) => Promise<GridRecord[]>;
  getWithoutContactTypeCount: (
    groupId: string,
    filterText?: string,
    filterColumns?: string,
    contactTypeId?: string,
  ) => Promise<number>;
}

export const ClientGroupContext = (
  api: DefaultApi,
): ClientGroupContextInterface => {
  return {
    getById: (id: string) => api.clientGroupControllerGet(id),
    getAllByPortion: async (
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientGroupControllerGetAllByPortion(
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(clientGroupForGrid);
    },
    getAllCount: (filterText?: string, filterColumns?: string) =>
      api.clientGroupControllerGetAllCount(
        filterText || '',
        filterColumns || '',
      ),
    create: (dto: CreateUpdateClientGroupDto) =>
      api.clientGroupControllerCreate(dto),
    update: (id: string, dto: CreateUpdateClientGroupDto) =>
      api.clientGroupControllerUpdate(id, dto),
    addClients: (groupId: string, clientIds: string[]) => {
      const dto: AddClientsToGroupDto = { clientIds };
      return api.clientGroupControllerAddClients(groupId, dto);
    },
    removeItem: (itemId: string) => api.clientGroupControllerRemoveItem(itemId),
    getAllByNamePattern: (pattern: string) =>
      api.clientGroupControllerGetAllByNamePattern(pattern),
    getAllByPortionForClient: async (
      clientId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
    ) => {
      const data = await api.clientGroupControllerGetAllByPortionForClient(
        clientId,
        pageSize,
        pageNumber,
        orderBy || '',
        orderDirection || '',
        filterText || '',
        filterColumns || '',
      );
      return data.map(clientGroupForGrid);
    },
    getAllCountForClient: (
      clientId: string,
      filterText?: string,
      filterColumns?: string,
    ) =>
      api.clientGroupControllerGetAllCountForClient(
        clientId,
        filterText || '',
        filterColumns || '',
      ),
    getLastEditor: (id: string) => api.clientGroupControllerGetLastEditor(id),
    getWithoutContactTypeByPortion: async (
      groupId: string,
      pageSize: number,
      pageNumber: number,
      orderBy?: string,
      orderDirection?: string,
      filterText?: string,
      filterColumns?: string,
      contactTypeId?: string,
    ) => {
      const data =
        await api.clientGroupControllerGetWithoutContactTypeByPortion(
          groupId,
          pageSize,
          pageNumber,
          orderBy || '',
          orderDirection || '',
          filterText || '',
          contactTypeId || '',
          filterColumns,
        );
      return data.map(clientBasicForGrid);
    },
    getWithoutContactTypeCount: (
      groupId: string,
      filterText?: string,
      filterColumns?: string,
      contactTypeId?: string,
    ) =>
      api.clientGroupControllerGetWithoutContactTypeCount(
        groupId,
        filterText || '',
        contactTypeId || '',
        filterColumns,
      ),
  };
};

export const newClientGroup = () => {
  return CreateUpdateClientGroupDtoFromJSON({});
};

export const convertClientGroup = (group?: GetClientGroupDto) => {
  if (!group) {
    return newClientGroup();
  }
  const converted = CreateUpdateClientGroupDtoFromJSON(group);
  return converted;
};

export const validate = (dto: CreateUpdateClientGroupDto) =>
  validateAgainst(ClientGroupSchema, dto);
